import { useMemo } from 'react';

import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import { getFeedbackSourceName, type SOURCE_TYPE } from '@/types/feedbackConst';

export type NameValue = {
  name: string;
  value: string | number | boolean;
};

export type FeedbackSourceInfo = {
  source: NameValue | null;
  domain: NameValue | null;
  channel: NameValue | null;
};

export type FeedbackSourceParams = {
  channel?: string | number;
  domain?: string | number;
  type: SOURCE_TYPE;
};

// FeedbackSourceT와 호환되는 파라미터 타입
export type FeedbackSourceParamsFromT = Pick<
  FeedbackSourceT,
  'type' | 'domain' | 'channel'
>;

/**
 * 피드백의 소스, 도메인, 채널 정보를 찾아서 반환하는 훅
 * @param params - 찾을 소스 정보의 파라미터
 * @returns 찾은 소스 정보와 표시할 텍스트
 */
export const useFeedbackSourceInfo = (params: FeedbackSourceParams) => {
  const sources = useFeedbackSourceStore((state) => state.sourceInfo);

  const sourceInfo = useMemo((): FeedbackSourceInfo | null => {
    if (!sources) return null;

    let targetSource: NameValue | null = null;
    let targetDomain: NameValue | null = null;
    let targetChannel: NameValue | null = null;

    // 3중 반복문으로 계층 구조 탐색
    for (const source of sources) {
      for (const domain of source.domains || []) {
        // 채널 찾기
        for (const channel of domain.channels || []) {
          if (channel.id === params.channel) {
            targetChannel = {
              name: channel.name,
              value: channel.id,
            };
          }
        }
        // 도메인 찾기
        if (domain.id === params.domain) {
          targetDomain = {
            name: domain.name,
            value: domain.id,
          };
        }
      }
    }

    // 소스는 매핑 테이블에서 찾기
    targetSource = {
      name: getFeedbackSourceName(params.type),
      value: params.type,
    };

    // 아무것도 찾지 못했으면 null 반환
    if (!targetSource && !targetDomain && !targetChannel) return null;

    return {
      source: targetSource,
      domain: targetDomain,
      channel: targetChannel,
    };
  }, [params.channel, params.domain, params.type, sources]);

  // 표시할 텍스트 결정 (우선순위: 채널 > 도메인 > 소스)
  const displayText = useMemo(() => {
    return (
      sourceInfo?.channel?.name ||
      sourceInfo?.domain?.name ||
      sourceInfo?.source?.name ||
      ''
    );
  }, [
    sourceInfo?.channel?.name,
    sourceInfo?.domain?.name,
    sourceInfo?.source?.name,
  ]);

  return {
    sourceInfo,
    displayText,
  };
};
